'use client';

import { useMutation } from '@tanstack/react-query';
import { Plus } from 'lucide-react';

import React, { useCallback, useEffect } from 'react';

import Image from 'next/image';

import AppLoader from '@/components/ui/app-loader';
import { Button } from '@/components/ui/button';

import { Avatar } from '@/shared/components';
import { toast } from '@/shared/components/toast';
import { MIXPANEL_APP_FEATURES, mixpanelEvents } from '@/shared/constants/mixpanel-events';
import { LogoutLink } from '@/shared/components/logout-link';
import { useAppCallbacks } from '@/shared/contexts/app-wrapper/providers/app-provider';
import { ISDiscoverableWorkspace, joinWorkspace } from '@/shared/services/workspace/discover';
import { emitMixpanelEvent } from '@/shared/utils/mixpanel-utils';

import {
	ONBOARDING_SCREEN_HEADERS,
	ONBOARDING_VIEW_WORKSPACE_JOIN,
} from '../../constants/user-onboarding-constants';
import { useUserOnboardingSafe } from '../../contexts/user-onboarding-provider';
import { ScreenLayout } from '../common/screen-layout';

type WorkspaceJoinScreenProps = {
	workspaces: ISDiscoverableWorkspace[];
	onCreateNewClick: () => void;
	onJoinSuccess?: (workspaceId: string) => void;
	skipLayout?: boolean;
	source?: string;
};

export function WorkspaceJoinScreen({
	workspaces,
	onCreateNewClick,
	onJoinSuccess,
	skipLayout: skipLayoutProp,
	source: sourceProp,
}: WorkspaceJoinScreenProps) {
	const onboardingContext = useUserOnboardingSafe();
	const goToNextScreen = onboardingContext?.goToNextScreen;
	const setWorkspaceName = onboardingContext?.setWorkspaceName;
	const skipLayout = skipLayoutProp ?? onboardingContext?.skipLayout ?? false;
	const source = sourceProp ?? onboardingContext?.source;
	const updateDefaultWorkspaceId = useAppCallbacks((state) => state.updateDefaultWorkspaceId);

	useEffect(() => {
		emitMixpanelEvent(mixpanelEvents.JOIN_WORKSPACE_SCREEN_SHOWED, {
			feature:
				source === MIXPANEL_APP_FEATURES.USER_ONBOARDING_VIA_MODAL
					? MIXPANEL_APP_FEATURES.USER_ONBOARDING_VIA_MODAL
					: MIXPANEL_APP_FEATURES.USER_ONBOARDING,
			type: source,
			workspace_count: workspaces.length,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps -- fire once on mount
	}, []);

	const {
		mutate: joinMutate,
		isPending: isJoinPending,
		variables: joinVariables,
	} = useMutation({
		mutationFn: joinWorkspace,
		onSuccess: async (data) => {
			await updateDefaultWorkspaceId(data.id);
			emitMixpanelEvent(mixpanelEvents.JOIN_WORKSPACE_SUCCESS, {
				feature:
					source === MIXPANEL_APP_FEATURES.USER_ONBOARDING_VIA_MODAL
						? MIXPANEL_APP_FEATURES.USER_ONBOARDING_VIA_MODAL
						: MIXPANEL_APP_FEATURES.USER_ONBOARDING,
				type: source,
				workspace_id: data.id,
			});
			if (onJoinSuccess) {
				onJoinSuccess(data.id);
			} else {
				const joinedWorkspace = workspaces.find((w) => w.id === data.id);
				if (joinedWorkspace) setWorkspaceName?.(joinedWorkspace.name);
				goToNextScreen?.();
			}
		},
		onError: () => {
			toast({
				title: 'Failed to join workspace',
				description: 'Please try again later',
				variant: 'destructive',
			});
		},
	});

	const handleJoin = useCallback(
		(workspaceId: string) => {
			if (isJoinPending) return;
			emitMixpanelEvent(mixpanelEvents.JOIN_WORKSPACE_CLICKED, {
				feature:
					source === MIXPANEL_APP_FEATURES.USER_ONBOARDING_VIA_MODAL
						? MIXPANEL_APP_FEATURES.USER_ONBOARDING_VIA_MODAL
						: MIXPANEL_APP_FEATURES.USER_ONBOARDING,
				type: source,
				workspace_id: workspaceId,
			});
			joinMutate({ page_id: workspaceId });
		},
		[isJoinPending, joinMutate, source]
	);

	const workspaceList = (
		<div className="flex w-full flex-col">
			{workspaces.map((workspace) => (
				<div
					key={workspace.id}
					className="flex min-w-0 items-center justify-between gap-3 border-b border-border/60 px-1 py-3 last:border-b-0"
				>
					<div className="flex min-w-0 items-center gap-3">
						{workspace.logo ? (
							<Image
								src={workspace.logo}
								alt={workspace.name}
								width={36}
								height={36}
								className="size-9 shrink-0 rounded-lg object-cover"
							/>
						) : (
							<Avatar
								name={workspace.name}
								className="size-9 shrink-0 rounded-lg"
								fallbackClassName="rounded-lg"
							/>
						)}
						<div className="min-w-0">
							<p className="truncate text-sm font-medium text-primary">{workspace.name}</p>
							<p className="text-xs text-secondary">
								{workspace.memberCount} {workspace.memberCount === 1 ? 'member' : 'members'}
							</p>
						</div>
					</div>
					<Button
						size="sm"
						className="shrink-0"
						onClick={() => handleJoin(workspace.id)}
						disabled={isJoinPending}
					>
						{isJoinPending && joinVariables?.page_id === workspace.id ? (
							<AppLoader className="h-4 w-4" />
						) : (
							'Join'
						)}
					</Button>
				</div>
			))}
		</div>
	);

	const handleCreateNewClick = useCallback(() => {
		emitMixpanelEvent(mixpanelEvents.CREATE_NEW_WORKSPACE_INSTEAD_CLICKED, {
			feature:
				source === MIXPANEL_APP_FEATURES.USER_ONBOARDING_VIA_MODAL
					? MIXPANEL_APP_FEATURES.USER_ONBOARDING_VIA_MODAL
					: MIXPANEL_APP_FEATURES.USER_ONBOARDING,
			type: source,
		});
		onCreateNewClick();
	}, [onCreateNewClick, source]);

	const createNewLink = (
		<Button
			type="button"
			variant="default"
			className="mt-4 w-full gap-2"
			onClick={handleCreateNewClick}
			disabled={isJoinPending}
		>
			<Plus className="size-4" />
			Create a new workspace
		</Button>
	);

	if (skipLayout) {
		return (
			<div className="flex w-full flex-col">
				<div className="no-scrollbar max-h-[40vh] overflow-y-auto">{workspaceList}</div>
				{createNewLink}
			</div>
		);
	}

	const cardContent = (
		<>
			<div className="no-scrollbar w-0 min-w-full max-h-[40vh] overflow-y-auto">{workspaceList}</div>
			{createNewLink}
		</>
	);

	return (
		<ScreenLayout
			title={ONBOARDING_SCREEN_HEADERS[ONBOARDING_VIEW_WORKSPACE_JOIN].title}
			description={ONBOARDING_SCREEN_HEADERS[ONBOARDING_VIEW_WORKSPACE_JOIN]?.description}
			cardContent={cardContent}
			contentCardClassName="[&>br]:hidden p-4"
			topRight={
				<LogoutLink />
			}
		/>
	);
}
